import {PushNotifications} from '@capacitor/push-notifications'
import {
  PushNotificationSchema as MobilePushNotification,
} from '@capacitor/push-notifications/dist/esm/definitions'
import {initializeApp} from 'firebase/app'
import {
  getMessaging,
  getToken,
  MessagePayload as WebPushNotification,
  onMessage,
} from 'firebase/messaging'
import {createContext, FC, ReactNode, useCallback, useEffect, useState} from 'react'
import {useTranslate} from 'react-admin'

import useBrowserNotifications from '../hooks/useBrowserNotifications'
import env from '../utils/env'
import {isNativeMobileApp} from '../utils/platform'

const PushNotificationContext = createContext<PushNotificationContextValues>({})

const PushNotificationsProvider: FC<PushNotificationsProviderProps> = ({
  children,
  disabled,
  onBackgroundPushNotificationActionPerformed,
  onForegroundPushNotificationReceived,
  onReceiveDeviceToken,
}) => {
  const {
    deviceToken, hasPermission, registrationError,
  } = useSetupPushNotification({
    disabled,
    onBackgroundPushNotificationActionPerformed,
    onForegroundPushNotificationReceived,
    onReceiveDeviceToken,
  })
  return (
    <PushNotificationContext.Provider
      value={{deviceToken, error: registrationError, hasPermission}}
    >
      {children}
    </PushNotificationContext.Provider>
  )
}

const useSetupPushNotification = ({
  disabled,
  onBackgroundPushNotificationActionPerformed,
  onForegroundPushNotificationReceived,
  onReceiveDeviceToken,
}: Omit<PushNotificationsProviderProps, 'children'>) => {
  const {
    deviceToken: mobileDeviceToken,
    hasPermission: hasMobilePermission,
    registrationError,
  } = useSetupMobilePushNotification({
    disabled: disabled || !isNativeMobileApp,
    onBackgroundPushNotificationActionPerformed,
    onForegroundPushNotificationReceived,
    onReceiveDeviceToken,
  })
  const {
    deviceToken: webDeviceToken,
    hasPermission: hasWebPermission,
  } = useSetupWebPushNotification({
    disabled: disabled || isNativeMobileApp,
    onBackgroundPushNotificationActionPerformed,
    onForegroundPushNotificationReceived,
    onReceiveDeviceToken,
  })
  return {
    deviceToken: isNativeMobileApp ? mobileDeviceToken : webDeviceToken,
    hasPermission: hasMobilePermission || hasWebPermission,
    registrationError,
  }
}

const useSetupMobilePushNotification = ({
  disabled,
  onBackgroundPushNotificationActionPerformed,
  onForegroundPushNotificationReceived,
  onReceiveDeviceToken,
}: Omit<PushNotificationsProviderProps, 'children'>) => {
  const translate = useTranslate()
  const [deviceToken, setDeviceToken] = useState<string | undefined>()
  const [registrationError, setRegistrationError] = useState<Error | undefined>()
  const [hasPermission, setHasPermission] = useState(false)
  const requestPermissions = useCallback(
    () => {
      // We could get the `{receive}` result already from the `requestPermissions()`
      // promise. However there is a bug in the push notification framework where the user
      // would tap "grant permissions" when request to grant push notification permission
      // but the callback here may still gave us surprisingly 'rejected'. Turns out that
      // when the app is then restarted, we properly get 'granted' reported back. Since we
      // want to know whether permissions have been granted already in the first app run,
      // we additionally call `checkPermissions()` which _will_ report the proper user
      // decision. This bug is prevalant on Lars' iPhone 8 with iOS 14.5.1 (he refuses to
      // upgrade to newer major iOS versions to avoid seeing these disturbing emojicons
      // depicting a pregnant man (a man!) in the iOS emoji picker).
      PushNotifications.requestPermissions()
        .then(() => PushNotifications.checkPermissions())
        .then(({receive}) => setHasPermission(receive === 'granted'))
        .catch(e => setRegistrationError((typeof e === 'string') ? new Error(e) : e))
    }, [setRegistrationError],
  )
  useEffect(
    () => {
      if (!hasPermission) return
      setDeviceToken(undefined)
      setRegistrationError(undefined)
      registerMobilePushNotifications({
        onBackgroundPushNotificationActionPerformed,
        onForegroundPushNotificationReceived,
        onRegistrationError: setRegistrationError,
        onRegistrationSuccess: setDeviceToken,
      })
      return () => {
        PushNotifications.removeAllListeners()
      }
    },
    [
      hasPermission,
      onBackgroundPushNotificationActionPerformed,
      onForegroundPushNotificationReceived,
      setDeviceToken,
      setRegistrationError,
    ],
  )
  useEffect(
    () => {
      if (!disabled && !deviceToken) requestPermissions()
    },
    [deviceToken, disabled, requestPermissions],
  )
  useEffect(
    () => {
      if (deviceToken) onReceiveDeviceToken(deviceToken)
    },
    [deviceToken, onReceiveDeviceToken],
  )
  useEffect(
    () => {
      registrationError &&
      console.error("Failed to request push notification permission", registrationError)
    },
    [registrationError, translate],
  )
  return {deviceToken, hasPermission, registrationError}
}


const useSetupWebPushNotification = ({
  disabled,
  onBackgroundPushNotificationActionPerformed,
  onForegroundPushNotificationReceived,
  onReceiveDeviceToken,
}: Omit<PushNotificationsProviderProps, 'children'>) => {
  const {hasPermission} = useBrowserNotifications()
  const [deviceToken, setDeviceToken] = useState<string | undefined>()
  useEffect(
    () => {
      if (disabled || !hasPermission || !VAPID_KEY) return
      const app = initializeApp(FIREBASE_CONFIG)
      const messaging = getMessaging(app)
      getToken(messaging, {vapidKey: VAPID_KEY})
        .then(token => setDeviceToken(token))
        // Ignore errors if we can't get a token
        .catch(() => {})
      const unsubscribeMessageListener = onMessage(
        messaging, payload => {
          if (!payload.data) return
          onForegroundPushNotificationReceived(payload)
        }
      )
      const messageNotificationclickHandler = event => {
        if (!event.data.action) return
        switch (event.data.action) {
          case 'REDIRECT_FROM_BACKGROUND_PUSH_NOTIFICATIONCLICK':
            onBackgroundPushNotificationActionPerformed(
              'NOTIFICATIONCLICK',
              event.data.payload
            )
        }
      }
      navigator.serviceWorker
        .addEventListener('message', messageNotificationclickHandler)
      return () => {
        unsubscribeMessageListener?.()
        navigator.serviceWorker
          .removeEventListener('message', messageNotificationclickHandler)
      }
    },
    [
      deviceToken,
      disabled,
      hasPermission,
      onBackgroundPushNotificationActionPerformed,
      onForegroundPushNotificationReceived,
      setDeviceToken,
    ],
  )
  useEffect(
    () => {
      deviceToken && onReceiveDeviceToken(deviceToken)
    },
    [deviceToken, onReceiveDeviceToken],
  )
  return {deviceToken, hasPermission}
}


const registerMobilePushNotifications = ({
  onBackgroundPushNotificationActionPerformed,
  onForegroundPushNotificationReceived,
  onRegistrationError,
  onRegistrationSuccess,
}) => {
  PushNotifications.addListener(
    'registration', ({value}) => onRegistrationSuccess(value),
  )
  PushNotifications.addListener(
    'registrationError', ({error}) => onRegistrationError(error),
  )
  PushNotifications.addListener(
    'pushNotificationReceived',
    notification => onForegroundPushNotificationReceived(notification),
  )
  PushNotifications.addListener(
    'pushNotificationActionPerformed',
    ({notification}) =>
      onBackgroundPushNotificationActionPerformed('NOTIFICATIONCLICK', notification),
  )
  PushNotifications.register()
}

const FIREBASE_CONFIG = {
  apiKey: "AIzaSyCqa-imZR1BADw1AcT4sJHVVLEIAiWf-iE",
  appId: "1:263588350977:web:93af65c1872d6d0a424400",
  authDomain: "flinkit-fd58e.firebaseapp.com",
  databaseURL: "https://flinkit-fd58e.firebaseio.com",
  measurementId: "G-WT7C2R3NQR",
  messagingSenderId: "263588350977",
  projectId: "flinkit-fd58e",
  storageBucket: "flinkit-fd58e.firebasestorage.app",
}
const VAPID_KEY = env('FCM_VAPID_KEY', null)

interface PushNotificationContextValues {
  deviceToken?: string
  error?: Error
  hasPermission?: boolean
}

interface PushNotificationsProviderProps {
  children: ReactNode
  disabled: boolean
  onBackgroundPushNotificationActionPerformed:
    (action: string, payload: PushNotification) => void
  onForegroundPushNotificationReceived: (payload: PushNotification) => void
  onReceiveDeviceToken: (deviceToken: string | undefined) => void
}

export type PushNotification = WebPushNotification | MobilePushNotification

export {PushNotificationContext, PushNotificationsProvider}
